import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiErrorMessage } from '@/components/analysis/reportAnalysisUi';
import {
  formatBid,
  formatDeltaPp,
  formatPosition,
  formatPositionDelta,
  formatCount,
  formatSearchVolume,
  formatSov,
  marketplaceLabel,
  splitKeywordInput,
} from '@/components/shareOfVoice/shareOfVoiceFormat';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui/Input';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { adsApi } from '@/api/client';
import { cn } from '@/utils/cn';
import type {
  SovKeywordAddResult,
  SovMetric,
  SovRankBy,
  SovTopCandidate,
} from '@/types/shareOfVoice';

function formatAcos(spend: number, sales: number): string {
  if (sales <= 0) return '—';
  return formatSov(spend / sales);
}

function fittingKeys(rows: SovTopCandidate[]): string[] {
  return rows.filter((row) => row.fits).map((row) => row.keywordKey);
}

function CandidateList({
  title,
  rows,
  currency,
  selectedKeys,
  onToggle,
  onToggleAll,
}: {
  title: string;
  rows: SovTopCandidate[];
  currency: string | null | undefined;
  selectedKeys: Set<string>;
  onToggle: (keywordKey: string) => void;
  onToggleAll: () => void;
}) {
  const selectable = fittingKeys(rows);
  const allSelected = selectable.length > 0 && selectable.every((key) => selectedKeys.has(key));
  return (
    <div className="space-y-2">
      <p className="text-label-md font-semibold uppercase tracking-[0.12em] text-foreground-subtle">{title}</p>
      {rows.length === 0 ? (
        <p className="text-body-sm text-foreground-muted">None in the last 30 days.</p>
      ) : (
        <TableWrap compact className="max-h-80 rounded-lg border border-border">
          <Table compact>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">
                  <label className="flex items-center gap-2 font-medium normal-case tracking-normal text-foreground">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={selectable.length === 0}
                      onChange={onToggleAll}
                      aria-label={`Select all ${title}`}
                    />
                    Select all
                  </label>
                </TableHead>
                <TableHead>Term</TableHead>
                <TableHead className="text-right">Spend</TableHead>
                <TableHead className="text-right">Sales</TableHead>
                <TableHead className="text-right">Orders</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">Impressions</TableHead>
                <TableHead className="text-right">ACOS</TableHead>
              </TableRow>
            </TableHeader>
            <tbody>
              {rows.map((row) => (
                <TableRow key={row.keywordKey}>
                  <TableCell compact>
                    <input
                      type="checkbox"
                      checked={row.fits && selectedKeys.has(row.keywordKey)}
                      disabled={!row.fits}
                      onChange={() => onToggle(row.keywordKey)}
                      aria-label={`Select ${row.keywordDisplay}`}
                    />
                  </TableCell>
                  <TableCell compact className={cn(!row.fits && 'text-foreground-muted')}>
                    {row.keywordDisplay}
                    {row.fits ? null : (
                      <span className="ml-2 text-foreground-subtle">Over cap</span>
                    )}
                  </TableCell>
                  <TableCell compact className="text-right tabular-nums">
                    {formatBid(row.spend, currency)}
                  </TableCell>
                  <TableCell compact className="text-right tabular-nums">
                    {formatBid(row.sales, currency)}
                  </TableCell>
                  <TableCell compact className="text-right tabular-nums">{formatCount(row.orders)}</TableCell>
                  <TableCell compact className="text-right tabular-nums">{formatCount(row.clicks)}</TableCell>
                  <TableCell compact className="text-right tabular-nums">{formatCount(row.impressions)}</TableCell>
                  <TableCell compact className="text-right tabular-nums">{formatAcos(row.spend, row.sales)}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}

function deltaClass(value: number | null, higherIsBetter: boolean): string {
  if (value == null || Math.abs(value) < 0.05) return 'text-foreground-muted';
  const positive = value > 0;
  const good = higherIsBetter ? positive : !positive;
  return good ? 'text-success-ink' : 'text-error-ink';
}

function KpiCard({
  label,
  value,
  delta,
  higherIsBetter,
}: {
  label: string;
  value: string;
  delta: string | null;
  higherIsBetter: boolean;
}) {
  const numeric = delta == null ? null : delta.startsWith('↓') ? -1 : delta.startsWith('↑') ? 1 : 0;
  return (
    <Card className="p-4">
      <p className="text-label-md font-semibold uppercase tracking-[0.12em] text-foreground-subtle">{label}</p>
      <p className="mt-2 text-headline-md font-semibold text-foreground">{value}</p>
      {delta ? (
        <p className={cn('mt-1 text-body-sm font-medium', deltaClass(numeric, higherIsBetter))}>{delta}</p>
      ) : (
        <p className="mt-1 text-body-sm text-foreground-muted">No prior snapshot</p>
      )}
    </Card>
  );
}

export function ShareOfVoicePage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('profiles.write');
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { range } = useAppDateRange();
  const startDate = range.current.startDate;
  const endDate = range.current.endDate;
  const [metric, setMetric] = useState<SovMetric>('combined');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [topOpen, setTopOpen] = useState(false);
  const [rankBy, setRankBy] = useState<SovRankBy>('sales');
  const [selectedImports, setSelectedImports] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const summaryQuery = useQuery({
    queryKey: ['sov-summary', profileId, startDate, endDate],
    queryFn: async () => {
      const res = await adsApi.getShareOfVoiceSummary(profileId, { startDate, endDate });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load share of voice');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  const listQuery = useQuery({
    queryKey: ['sov-keywords', profileId],
    queryFn: async () => {
      const res = await adsApi.getShareOfVoiceKeywords(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load keywords');
      return res.data;
    },
    enabled: Boolean(profileId),
  });

  const importQuery = useQuery({
    queryKey: ['sov-import', profileId],
    queryFn: async () => {
      const res = await adsApi.getShareOfVoiceImportCandidates(profileId);
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load ad keywords');
      return res.data.keywords;
    },
    enabled: Boolean(profileId) && importOpen && canWrite,
  });

  const topQuery = useQuery({
    queryKey: ['sov-top', profileId, rankBy],
    queryFn: async () => {
      const res = await adsApi.getShareOfVoiceTopCandidates(profileId, { rankBy });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to load top terms');
      return res.data;
    },
    enabled: Boolean(profileId) && topOpen && canWrite,
  });

  useEffect(() => {
    const preview = topQuery.data;
    if (!preview) return;
    setSelectedKeys(
      new Set(
        [...preview.keywords, ...preview.searchTerms].filter((row) => row.fits).map((row) => row.keywordKey)
      )
    );
  }, [topQuery.data]);

  const addMutation = useMutation({
    mutationFn: async (input: { keywords: string[]; deferFetch?: boolean }) => {
      const res = await adsApi.addShareOfVoiceKeywords(profileId, input.keywords, {
        deferFetch: input.deferFetch,
      });
      if (!res.success || !res.data) throw new Error(res.message || 'Failed to add keywords');
      return res.data;
    },
    onSuccess: (result: SovKeywordAddResult, input) => {
      void queryClient.invalidateQueries({ queryKey: ['sov-keywords', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['sov-summary', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['sov-import', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['sov-top', profileId] });
      const added = result.added.length + result.reactivated.length;
      if (added && input.deferFetch) {
        showToast(
          `Tracking ${added} term${added === 1 ? '' : 's'}. Snapshots fill in as they are fetched.`,
          'success'
        );
      } else if (added) showToast(`Tracking ${added} keyword${added === 1 ? '' : 's'}`, 'success');
      else if (result.rejected.length) showToast(`Limit is ${result.cap} keywords for this account`, 'error');
      else if (result.duplicates.length) showToast('Those keywords are already tracked', 'error');
      setDraft('');
      setSelectedImports([]);
    },
    onError: (error: unknown) => showToast(apiErrorMessage(error, 'Failed to add keywords'), 'error'),
  });

  const removeMutation = useMutation({
    mutationFn: async (keywordKey: string) => {
      const res = await adsApi.removeShareOfVoiceKeyword(profileId, keywordKey);
      if (!res.success) throw new Error(res.message || 'Failed to remove keyword');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sov-keywords', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['sov-summary', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['sov-import', profileId] });
      void queryClient.invalidateQueries({ queryKey: ['sov-top', profileId] });
      showToast('Keyword removed', 'success');
    },
    onError: (error: unknown) => showToast(apiErrorMessage(error, 'Failed to remove keyword'), 'error'),
  });

  const onAdd = (event: FormEvent) => {
    event.preventDefault();
    const keywords = splitKeywordInput(draft);
    if (!keywords.length) {
      showToast('Enter at least one keyword', 'error');
      return;
    }
    addMutation.mutate({ keywords });
  };

  const toggleTopKey = (keywordKey: string) => {
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(keywordKey)) next.delete(keywordKey);
      else next.add(keywordKey);
      return next;
    });
  };

  const toggleTopList = (rows: SovTopCandidate[]) => {
    const keys = fittingKeys(rows);
    const allSelected = keys.length > 0 && keys.every((key) => selectedKeys.has(key));
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (allSelected) keys.forEach((key) => next.delete(key));
      else keys.forEach((key) => next.add(key));
      return next;
    });
  };

  const checkedTopTerms = () => {
    const preview = topQuery.data;
    if (!preview) return [];
    return [...preview.keywords, ...preview.searchTerms].filter(
      (row) => row.fits && selectedKeys.has(row.keywordKey)
    );
  };

  const trackTopTerms = () => {
    const keywords = checkedTopTerms().map((row) => row.keywordDisplay);
    if (!keywords.length) {
      showToast('Nothing new to track in this preview', 'error');
      return;
    }
    addMutation.mutate({ keywords, deferFetch: true });
  };

  const data = summaryQuery.data;
  const kpis = data?.kpis;
  const query = search.trim().toLowerCase();
  const rows = (data?.keywords ?? []).filter((row) =>
    query ? row.keywordDisplay.toLowerCase().includes(query) : true
  );
  const highlight = (column: SovMetric) => column === metric;
  const analysisTo = `/accounts/${encodeURIComponent(profileId)}/campaigns/analysis`;

  return (
    <div className="mx-auto max-w-7xl space-y-5 bg-page px-4 py-6 min-h-full">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-headline-md font-semibold text-foreground">Share of voice</h1>
          <p className="mt-1 text-sm text-foreground-muted">
            {data ? `${marketplaceLabel(data.marketplace)} · ` : ''}
            Weighted share of voice from the latest daily snapshot. Search volume and suggested bid are a
            30-day window.
          </p>
        </div>
        {listQuery.data ? (
          <p className="text-body-sm text-foreground-muted">
            {listQuery.data.activeCount} of {listQuery.data.cap} keywords
          </p>
        ) : null}
      </div>

      {data && !data.hasBrandNames ? (
        <Card className="border-warning bg-warning-tint px-4 py-3 text-body-sm text-foreground">
          Set your brand names on{' '}
          <Link to={analysisTo} className="font-semibold text-brand-deep hover:underline">
            Analysis
          </Link>{' '}
          so these cards can show your organic, paid, and combined share of voice.
        </Card>
      ) : null}

      {data && !data.marketplace ? (
        <Card className="px-4 py-3 text-body-sm text-foreground-muted">
          This account&apos;s marketplace is not covered by share-of-voice sync. Supported countries are
          US, UK, DE, IN, CA, FR, IT, ES, MX, and JP.
        </Card>
      ) : null}

      {summaryQuery.isLoading ? (
        <div className="flex items-center gap-2 text-foreground-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading share of voice
        </div>
      ) : summaryQuery.error ? (
        <p className="text-body-sm text-error-ink">{apiErrorMessage(summaryQuery.error, 'Failed to load share of voice')}</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Organic SOV"
            value={formatSov(kpis?.organicSov)}
            delta={formatDeltaPp(kpis?.organicSovDeltaPp)}
            higherIsBetter
          />
          <KpiCard
            label="Paid SOV"
            value={formatSov(kpis?.paidSov)}
            delta={formatDeltaPp(kpis?.paidSovDeltaPp)}
            higherIsBetter
          />
          <KpiCard
            label="Combined SOV"
            value={formatSov(kpis?.combinedSov)}
            delta={formatDeltaPp(kpis?.combinedSovDeltaPp)}
            higherIsBetter
          />
          <KpiCard
            label="Avg position"
            value={formatPosition(kpis?.avgPosition)}
            delta={formatPositionDelta(kpis?.avgPositionDelta)}
            higherIsBetter
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search keywords"
          aria-label="Search keywords"
          className="max-w-xs"
        />
        <Select
          value={metric}
          onChange={(event) => setMetric(event.target.value as SovMetric)}
          aria-label="SOV type"
        >
          <option value="combined">Combined</option>
          <option value="organic">Organic</option>
          <option value="paid">Paid</option>
        </Select>
      </div>

      {canWrite ? (
        <Card className="space-y-3 p-4">
          <form onSubmit={onAdd} className="flex flex-wrap items-center gap-2">
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Add keywords, separated by commas"
              aria-label="Keywords to track"
              className="min-w-[16rem] flex-1"
            />
            <Button type="submit" disabled={addMutation.isPending}>
              {addMutation.isPending && !addMutation.variables?.deferFetch ? 'Fetching…' : 'Add'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setImportOpen((open) => !open)}>
              Import from ad keywords
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setTopOpen((open) => !open)}
            >
              Add top terms
            </Button>
          </form>
          {topOpen ? (
            <div className="space-y-3 border-t border-border pt-3">
              <div className="flex flex-wrap items-center gap-3">
                <Select
                  value={rankBy}
                  onChange={(event) => setRankBy(event.target.value as SovRankBy)}
                  aria-label="Rank top terms by"
                >
                  <option value="sales">Sales</option>
                  <option value="spend">Spend</option>
                  <option value="orders">Orders</option>
                  <option value="clicks">Clicks</option>
                  <option value="impressions">Impressions</option>
                </Select>
                <p className="text-body-sm text-foreground-muted">
                  {topQuery.data
                    ? `Last 30 days, ${topQuery.data.startDate} to ${topQuery.data.endDate}.`
                    : 'Last 30 days.'}
                </p>
              </div>
              {topQuery.isLoading ? (
                <p className="text-body-sm text-foreground-muted">Loading top terms…</p>
              ) : topQuery.error ? (
                <p className="text-body-sm text-error-ink">
                  {apiErrorMessage(topQuery.error, 'Failed to load top terms')}
                </p>
              ) : topQuery.data ? (
                <>
                  <div className="space-y-4">
                    <CandidateList
                      title="Keywords"
                      rows={topQuery.data.keywords}
                      currency={data?.currencyCode}
                      selectedKeys={selectedKeys}
                      onToggle={toggleTopKey}
                      onToggleAll={() => toggleTopList(topQuery.data?.keywords ?? [])}
                    />
                    <CandidateList
                      title="Search terms"
                      rows={topQuery.data.searchTerms}
                      currency={data?.currencyCode}
                      selectedKeys={selectedKeys}
                      onToggle={toggleTopKey}
                      onToggleAll={() => toggleTopList(topQuery.data?.searchTerms ?? [])}
                    />
                  </div>
                  <p className="text-body-sm text-foreground-muted">
                    {topQuery.data.keywordCount} keywords and {topQuery.data.searchTermCount} search terms.{' '}
                    {topQuery.data.alreadyTracked} already tracked. {topQuery.data.overCap} over the{' '}
                    {topQuery.data.cap} cap.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    disabled={checkedTopTerms().length === 0 || addMutation.isPending}
                    onClick={trackTopTerms}
                  >
                    {addMutation.isPending && addMutation.variables?.deferFetch
                      ? 'Tracking…'
                      : `Track these (${checkedTopTerms().length})`}
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
          {importOpen ? (
            <div className="space-y-2">
              {importQuery.isLoading ? (
                <p className="text-body-sm text-foreground-muted">Loading enabled ad keywords…</p>
              ) : importQuery.error ? (
                <p className="text-body-sm text-error-ink">{apiErrorMessage(importQuery.error, 'Failed to load ad keywords')}</p>
              ) : (importQuery.data ?? []).length === 0 ? (
                <p className="text-body-sm text-foreground-muted">No enabled ad keywords are left to import.</p>
              ) : (
                <>
                  <div className="max-h-48 space-y-1 overflow-y-auto">
                    {(importQuery.data ?? []).map((candidate) => (
                      <label key={candidate.keywordKey} className="flex items-center gap-2 text-body-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={selectedImports.includes(candidate.keywordDisplay)}
                          onChange={(event) => {
                            setSelectedImports((current) =>
                              event.target.checked
                                ? [...current, candidate.keywordDisplay]
                                : current.filter((item) => item !== candidate.keywordDisplay)
                            );
                          }}
                        />
                        {candidate.keywordDisplay}
                      </label>
                    ))}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!selectedImports.length || addMutation.isPending}
                    onClick={() => addMutation.mutate({ keywords: selectedImports })}
                  >
                    {addMutation.isPending ? 'Fetching…' : 'Track selected'}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </Card>
      ) : null}

      <Card>
        <TableWrap>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead className="text-right">Search volume</TableHead>
                <TableHead className={cn('text-right', highlight('organic') && 'bg-surface-muted')}>Organic SOV</TableHead>
                <TableHead className={cn('text-right', highlight('paid') && 'bg-surface-muted')}>Paid SOV</TableHead>
                <TableHead className={cn('text-right', highlight('combined') && 'bg-surface-muted')}>Combined SOV</TableHead>
                <TableHead className={cn('text-right', highlight('organic') && 'bg-surface-muted')}>Organic pos.</TableHead>
                <TableHead className={cn('text-right', highlight('paid') && 'bg-surface-muted')}>Paid pos.</TableHead>
                <TableHead className="text-right">Suggested bid</TableHead>
                {canWrite ? <TableHead /> : null}
              </TableRow>
            </TableHeader>
            <tbody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 9 : 8} className="text-foreground-muted">
                    {summaryQuery.isLoading
                      ? 'Loading keywords…'
                      : summaryQuery.error
                        ? 'Share of voice could not be loaded.'
                        : data?.keywords.length
                          ? 'No keywords match that search.'
                          : 'No keywords are tracked yet. Add the terms you want in the daily snapshot.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.keywordKey}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/accounts/${encodeURIComponent(profileId)}/campaigns/sov/${encodeURIComponent(row.keywordKey)}`
                      )
                    }
                  >
                    <TableCell className="font-medium">
                      {row.keywordDisplay}
                      {row.snapshotDate ? null : (
                        <span className="mt-0.5 block text-body-sm font-normal text-foreground-muted">No snapshot in this date range</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatSearchVolume(row.searchVolume30d)}</TableCell>
                    <TableCell className={cn('text-right tabular-nums', highlight('organic') && 'bg-surface-muted')}>
                      {formatSov(row.organicSov)}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', highlight('paid') && 'bg-surface-muted')}>
                      {formatSov(row.paidSov)}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', highlight('combined') && 'bg-surface-muted')}>
                      {formatSov(row.combinedSov)}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', highlight('organic') && 'bg-surface-muted')}>
                      {formatPosition(row.organicPosition)}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', highlight('paid') && 'bg-surface-muted')}>
                      {formatPosition(row.paidPosition)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatBid(row.suggestedBidMedian, data?.currencyCode)}
                    </TableCell>
                    {canWrite ? (
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeMutation.mutate(row.keywordKey);
                          }}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>
      </Card>
    </div>
  );
}
